import React from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface PricingTier {
  name: string;
  price: number;
  description: string;
  addressRange: string;
  features: string[];
  popular?: boolean;
}

const Pricing: React.FC = () => {
  const navigate = useNavigate();

  const pricingTiers: PricingTier[] = [
    {
      name: 'STARTER',
      price: 49,
      description: 'Perfect for small property managers',
      addressRange: 'Up to 25 properties',
      features: [
        'Property groups and units',
        'Resident and vendor management',
        'Invoices and PDF receipts',
        'Online invoice payments via Stripe Checkout',
        'Maintenance requests',
        'Documents, announcements, and events',
        'Expense and cash-flow tracking',
      ]
    },
    {
      name: 'GROWTH',
      price: 99,
      description: 'Ideal for growing property portfolios',
      addressRange: '26-100 properties',
      features: [
        'Property groups and units',
        'Resident and vendor management',
        'Invoices and PDF receipts',
        'Online invoice payments via Stripe Checkout',
        'Maintenance requests',
        'Documents, announcements, and events',
        'Expense and cash-flow tracking',
      ],
      popular: true
    },
    {
      name: 'PROFESSIONAL',
      price: 179,
      description: 'For larger property portfolios',
      addressRange: '101-500 properties',
      features: [
        'Property groups and units',
        'Resident and vendor management',
        'Invoices and PDF receipts',
        'Online invoice payments via Stripe Checkout',
        'Maintenance requests',
        'Documents, announcements, and events',
        'Expense and cash-flow tracking',
      ]
    },
    {
      name: 'ENTERPRISE',
      price: 250,
      description: 'For large-scale property management',
      addressRange: 'Unlimited properties',
      features: [
        'Property groups and units',
        'Resident and vendor management',
        'Invoices and PDF receipts',
        'Online invoice payments via Stripe Checkout',
        'Maintenance requests',
        'Documents, announcements, and events',
        'Expense and cash-flow tracking',
      ]
    }
  ];

  const handleSelectPlan = (tier: PricingTier) => {
    navigate('/signup', { state: { selectedPlan: tier } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Plans are based on the number of properties you manage. Every plan includes the same core
            BlockBoard tools; choose the capacity that fits your portfolio.
          </p>
          <p className="text-base text-gray-400 max-w-3xl mx-auto mt-4">
            Every plan starts with a <span className="text-white font-medium">14-day trial</span>.
            Payment details are collected at checkout, and billing begins after the trial unless you
            cancel.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {pricingTiers.map((tier, index) => (
            <div
              key={tier.name}
              className={`relative rounded-2xl p-8 ${
                tier.popular
                  ? 'bg-gradient-to-b from-[#22d3ee] to-[#06b6d4] text-white transform scale-105'
                  : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
              } transition-all duration-300 hover:transform hover:scale-105`}
            >
              {tier.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-yellow-500 text-black px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-2">{tier.name}</h3>
                <p className={`text-sm ${tier.popular ? 'text-white/80' : 'text-gray-400'} mb-4`}>
                  {tier.description}
                </p>
                <div className="mb-2">
                  <span className="text-4xl font-bold">${tier.price}</span>
                  <span className={`text-lg ${tier.popular ? 'text-white/80' : 'text-gray-400'}`}>
                    /month
                  </span>
                </div>
                <p className={`text-sm font-medium ${tier.popular ? 'text-white/90' : 'text-gray-300'}`}>
                  {tier.addressRange}
                </p>
              </div>

              <ul className="space-y-4 mb-8">
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start">
                    <CheckIcon className={`h-5 w-5 ${tier.popular ? 'text-white' : 'text-green-400'} mr-3 mt-0.5 flex-shrink-0`} />
                    <span className={`text-sm ${tier.popular ? 'text-white/90' : 'text-gray-300'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(tier)}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                  tier.popular
                    ? 'bg-white text-[#22d3ee] hover:bg-gray-100'
                    : 'bg-[#22d3ee] text-white hover:bg-[#06b6d4]'
                }`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-8">
            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-3">
                Can I change plans at any time?
              </h3>
              <p className="text-gray-300">
                Plan changes are available from the subscription area for Stripe-backed accounts.
                If your account is managed directly by BlockBoard, contact support for plan changes.
              </p>
            </div>

            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-3">
                What happens if I exceed my address limit?
              </h3>
              <p className="text-gray-300">
                You will need to move to a plan with a higher property capacity before adding
                properties beyond your current limit.
              </p>
            </div>

            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-3">
                Is there a trial available?
              </h3>
              <p className="text-gray-300">
                Yes. Every plan starts with a 14-day trial. Payment details are collected at
                checkout, and the regular monthly rate is charged after the trial unless you cancel.
                A paid subscription is required to continue after the trial.
              </p>
            </div>

            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <h3 className="text-xl font-semibold text-white mb-3">
                Do you offer annual billing discounts?
              </h3>
              <p className="text-gray-300">
                All plans are billed monthly today. If you're interested in annual billing or custom enterprise pricing, contact our sales team and we'll work something out.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to streamline your property management?
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Start with the tools you need to manage properties, residents, invoices, maintenance,
            documents, and expenses in one place.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="bg-[#22d3ee] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#06b6d4] transition-colors"
          >
            Start Your 14-Day Trial
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pricing;